import { ICapitalGood } from '../capital-good/capital-good.model';
import { ICategory } from '../category/category.model';
import { IConversionSource } from '../conversion-source/conversion-source.model';
import { IConversion } from '../conversion/conversion.model';
import { IDataTypeRelation } from '../data-types/data-types.model';
import { IEnergySupplierTariffDetails } from '../energy-supplier-tariff-details/energy-supplier-tariff-details.model';
import { IGoodCategory } from '../good-category/good-category.model';
import { IGroup } from '../group/group.model';
import { IManufacturer } from '../manufacturer/manufacturer.model';
import { IReneweableEnergyCertificate } from '../renewable-energy-certificate/renewable-energy-certificate.model';
import { ISpendBasedConversion } from '../spend-based-conversion/spend-based-conversion.model';
import { IGreenHouseGasUnit, IMeasurementUnit, ITransactionCurrencyConversion, ITransactionEnergyBreakdown, ITransactionGreenHouseGasBreakdown } from '../transaction/transaction.model';

export type EmissionCalculationType =
  | 'SPEND'
  | 'DIRECT'
  | 'ACTIVITY'
  | 'PRODUCT'
  | 'MEASUREMENT';

export type MarketBasedSourceType =
  | 'CUSTOM'
  | 'RESIDUAL_FUEL_MIX_ID'
  | 'ENERGY_SUPPLIER_TARIFF_DETAIL_ID'
  | 'CONVERSION_ID';

export type FlagCategory =
  | 'AGRICULTURE'
  | 'FORESTRY'
  | 'LAND_USE_CHANGE'
  | 'LAND_MANAGEMENT'
  | 'REMOVALS';

export type ProvisionalReason = 'FACTOR_YEAR_PAST' | 'FACTOR_YEAR_FUTURE';

/** Label for a provisional-factor chip; distinguishes the substitution direction. */
export const provisionalFactorLabel = (reason?: ProvisionalReason | string | null): string => {
  if (reason === 'FACTOR_YEAR_FUTURE') return 'Provisional (later-year factor)';
  if (reason === 'FACTOR_YEAR_PAST') return 'Provisional (prior-year factor)';
  return 'Provisional';
};

/** Label for an estimated (floor-area / GHG-Protocol average-data) calculation chip. */
export const estimatedFactorLabel = (reason?: string | null): string => {
  if (reason === 'FLOOR_AREA') return 'Estimated (floor area)';
  return 'Estimated';
};

export interface IEmissionCalculation {
  readonly id: string;
  readonly emissionId: string;
  readonly type: EmissionCalculationType;
  readonly calendarYear: number;
  readonly startDate: string;
  readonly endDate: string;
  readonly unitsConsumed: number;
  readonly originalUnitsConsumed?: number;
  readonly measurementUnitAmount: number;
  readonly measurementUnitId: string;
  readonly measurementUnit: IMeasurementUnit;
  readonly emissionAmount: number;
  readonly outcomeAmount: number;
  readonly outcomeUnitId: string;
  readonly outcomeUnitMeasurementUnit: IMeasurementUnit;
  readonly outcomeUnitGreenHouseGasUnit: IGreenHouseGasUnit;
  readonly greenHouseGasUnitId: string;
  readonly greenHouseGasUnit: IGreenHouseGasUnit;
  readonly multiplier: number;
  readonly scope: string;
  readonly groupId: string;
  readonly group: IGroup;
  readonly categoryId?: string | null;
  readonly category: ICategory;
  readonly goodCategory: IGoodCategory;
  readonly categoryModelName?: string | null;
  readonly conversionId?: string | null;
  readonly conversion: IConversion;
  readonly spendBasedConversion: ISpendBasedConversion;
  readonly emission?: { readonly nominalCode?: string | null; readonly nominalDescription?: string | null } | null;
  readonly conversionModelName?: string | null;
  readonly marketBasedSource?: MarketBasedSourceType | null;
  readonly marketBasedSourceId?: string | null;
  readonly manufactuerId?: string | null;
  readonly manufacturer: IManufacturer;
  readonly capitalGoodId?: string | null;
  readonly capitalGood: ICapitalGood;
  readonly relatedModelId: string | null;
  readonly relatedModelName: string | null;
  readonly thirdPartyConnectionImportLineId?: string | null;
  readonly reportActionId?: string | null;
  readonly dataTypeId: string;
  readonly dataType: IDataTypeRelation;
  readonly isMarketBased: boolean;
  readonly isProvisional: boolean;
  readonly provisionalReason?: ProvisionalReason | null;
  readonly isEstimated?: boolean;
  readonly estimatedReason?: string | null;
  readonly isWtt: boolean;
  /**
   * Display only. The activity ought to carry upstream emissions but the conversion
   * source publishes no matching factor, so the footprint is knowably incomplete here.
   */
  readonly upstreamFactorMissing?: boolean;
  readonly isSelected: boolean;
  readonly isFlag?: boolean;
  /** TICKET-E: biogenic combustion CO2 — excluded from scope totals, reported "outside of scopes". */
  readonly isBiogenic?: boolean;
  readonly flagCategory?: FlagCategory | null;
  readonly inflationAdjustmentApplied?: boolean;
  readonly transactionCurrencyConversions: ITransactionCurrencyConversion[];
  readonly residualFuelMix: IResidualFuelMix;
  readonly marketBasedConversion: IConversion;
  readonly energySupplierTariffDetail: IEnergySupplierTariffDetails;
  readonly transactionGreenHouseGasBreakdowns: ITransactionGreenHouseGasBreakdown[];
  readonly transactionEnergyBreakdowns: ITransactionEnergyBreakdown[];
  readonly transactionRenewableEnergyCertificates: IReneweableEnergyCertificate[];
  readonly squareFootConversion?: ISquareFootConversion;
}


export interface ISquareFootConversion {
  readonly id: string;
  readonly consumptionTypeCode: string;
  readonly multiplier: number;
  readonly calendarYear: number;
  readonly conversionSource: { source: string };
}

export interface IResidualFuelMix {
  readonly id: string;
  readonly multiplier: number;
  readonly active: boolean;
  readonly scope: string;
  readonly conversionSourceId: string;
  readonly conversionSource: IConversionSource;
  readonly calendarYear: number;
}
